<template>
  <div class="media-search">
    <section class="media-hero">
      <div class="media-hero__copy">
        <span class="media-hero__badge">素材搜索</span>
        <h2>图片 / 视频素材搜索</h2>
        <p>
          你可以切换不同素材库，再按图片或视频搜索，并直接在线预览结果。
        </p>
        <div class="media-hero__note">
          快捷词显示中文，但实际搜索时会用对应英文关键词发给平台。
        </div>
      </div>

      <div class="media-hero__stats">
        <div class="media-stat">
          <strong>{{ searched ? formatNumber(meta.totalHits || 0) : '--' }}</strong>
          <span>当前结果数</span>
        </div>
        <div class="media-stat">
          <strong>{{ activeModeLabel }}</strong>
          <span>当前搜索类型</span>
        </div>
        <div class="media-stat">
          <strong>{{ loading ? '搜索中' : (meta.queryUsed || '可留空浏览') }}</strong>
          <span>实际搜索词</span>
        </div>
      </div>
    </section>

    <section class="media-panel">
      <div class="media-toolbar">
        <el-input
          v-model.trim="form.q"
          clearable
          size="medium"
          placeholder="输入中文或英文关键词，可留空直接浏览推荐素材"
          @keyup.enter.native="search(true)"
        >
          <el-button
            slot="append"
            type="primary"
            icon="el-icon-search"
            :loading="loading"
            @click="search(true)"
          >
            搜索素材
          </el-button>
        </el-input>

        <div class="media-filters">
          <el-radio-group v-model="form.source" size="small" @change="onSourceChange">
            <el-radio-button label="pexels">优选素材库</el-radio-button>
            <el-radio-button label="pixabay">扩展素材库</el-radio-button>
          </el-radio-group>

          <el-radio-group v-model="form.type" size="small" @change="onTypeChange">
            <el-radio-button label="image">图片</el-radio-button>
            <el-radio-button label="video">视频</el-radio-button>
          </el-radio-group>

          <el-select v-model="form.perPage" size="small" class="media-filter-select" @change="search(true)">
            <el-option label="每页 24 条" :value="24" />
            <el-option label="每页 40 条" :value="40" />
            <el-option label="每页 60 条" :value="60" />
          </el-select>

          <template v-if="isPixabay">
            <el-select v-model="form.order" size="small" class="media-filter-select" @change="search(true)">
              <el-option label="热门优先" value="popular" />
              <el-option label="最新优先" value="latest" />
            </el-select>

            <el-select v-model="form.category" size="small" class="media-filter-select" @change="search(true)">
              <el-option label="全部分类" value="" />
              <el-option
                v-for="item in pixabayCategoryOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>

            <el-select
              v-if="isImageMode"
              v-model="form.imageType"
              size="small"
              class="media-filter-select"
              @change="search(true)"
            >
              <el-option label="照片" value="photo" />
              <el-option label="插画" value="illustration" />
              <el-option label="矢量图" value="vector" />
              <el-option label="全部图片" value="all" />
            </el-select>

            <el-select
              v-if="isImageMode"
              v-model="form.orientation"
              size="small"
              class="media-filter-select"
              @change="search(true)"
            >
              <el-option label="全部方向" value="all" />
              <el-option label="横图" value="horizontal" />
              <el-option label="竖图" value="vertical" />
            </el-select>

            <el-select
              v-if="!isImageMode"
              v-model="form.videoType"
              size="small"
              class="media-filter-select"
              @change="search(true)"
            >
              <el-option label="全部视频" value="all" />
              <el-option label="实拍视频" value="film" />
              <el-option label="动画视频" value="animation" />
            </el-select>

            <el-switch
              v-model="form.safeSearch"
              active-text="安全搜索"
              inactive-text="不过滤"
              @change="search(true)"
            />
          </template>

          <template v-else>
            <el-select
              v-model="form.pexelsOrientation"
              size="small"
              class="media-filter-select"
              @change="search(true)"
            >
              <el-option label="全部方向" value="" />
              <el-option label="横向" value="landscape" />
              <el-option label="纵向" value="portrait" />
              <el-option label="方图" value="square" />
            </el-select>

            <el-select
              v-model="form.pexelsSize"
              size="small"
              class="media-filter-select"
              @change="search(true)"
            >
              <el-option label="全部尺寸" value="" />
              <el-option label="大图" value="large" />
              <el-option label="中图" value="medium" />
              <el-option label="小图" value="small" />
            </el-select>

            <el-select
              v-model="form.pexelsLocale"
              size="small"
              class="media-filter-select"
              @change="search(true)"
            >
              <el-option label="中文" value="zh-CN" />
              <el-option label="英文" value="en-US" />
              <el-option label="日文" value="ja-JP" />
            </el-select>

            <el-select
              v-if="isImageMode"
              v-model="form.pexelsColor"
              clearable
              size="small"
              class="media-filter-select"
              @change="search(true)"
            >
              <el-option
                v-for="item in pexelsColorOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </template>
        </div>
      </div>

      <el-alert
        v-if="isPixabay"
        title="当前素材库更建议输入英文关键词搜索；如果不想手动输入英文，可以直接点击下方按钮搜索。"
        type="warning"
        :closable="false"
        show-icon
        class="media-alert"
      />

      <div class="media-advanced">
        <template v-if="isPixabay">
          <div class="media-advanced__group">
            <span class="media-advanced__label">高级筛选</span>
            <el-switch
              v-model="form.editorsChoice"
              active-text="编辑精选"
              inactive-text="全部结果"
              @change="search(true)"
            />
          </div>

          <div class="media-advanced__group">
            <span class="media-advanced__label">最小尺寸</span>
            <el-input-number v-model="form.minWidth" :min="0" :step="100" size="small" @change="search(true)" />
            <span class="media-advanced__sep">×</span>
            <el-input-number v-model="form.minHeight" :min="0" :step="100" size="small" @change="search(true)" />
          </div>

          <div v-if="isImageMode" class="media-advanced__group media-advanced__group--wide">
            <span class="media-advanced__label">颜色</span>
            <el-select
              v-model="form.colors"
              multiple
              collapse-tags
              size="small"
              placeholder="不限颜色"
              class="media-color-select"
              @change="search(true)"
            >
              <el-option
                v-for="item in pixabayColorOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>
        </template>

        <template v-else>
          <div class="media-advanced__tip">
            当前素材库支持按方向、尺寸、颜色和语言筛选。
          </div>
        </template>
      </div>

      <div class="media-quick">
        <button
          v-for="item in quickKeywords"
          :key="item.label"
          type="button"
          class="media-quick__chip"
          @click="applyQuickKeyword(item)"
        >
          {{ item.label }}
        </button>
      </div>

      <div v-if="translationSummary" class="media-translation">
        <i class="el-icon-connection"></i>
        <span>{{ translationSummary }}</span>
      </div>
    </section>

    <section v-loading="loading" class="media-results">
      <el-empty
        v-if="searched && !loading && !items.length"
        description="没有找到匹配素材，可以换关键词或者放宽筛选再试"
        class="media-empty"
      />

      <div v-else-if="items.length" class="media-grid">
        <article
          v-for="item in items"
          :key="`${form.source}-${item.id}`"
          class="media-card"
          @click="openPreview(item)"
        >
          <div class="media-card__media">
            <img
              v-if="item.mediaType === 'image'"
              :src="item.previewUrl"
              :alt="item.title"
              loading="lazy"
            />
            <div
              v-else
              class="media-card__video"
              :style="videoPosterStyle(item)"
            >
              <span class="media-card__play">
                <i class="el-icon-video-play"></i>
                在线预览
              </span>
            </div>
          </div>

          <div class="media-card__body">
            <div class="media-card__top">
              <el-tag size="mini" effect="plain">{{ item.mediaType === 'image' ? '图片' : '视频' }}</el-tag>
            </div>
            <h3>{{ item.title }}</h3>
            <p v-if="item.tags" class="media-card__tags">{{ item.tags }}</p>
            <div class="media-card__meta">
              <span v-if="item.width && item.height">{{ item.width }} × {{ item.height }}</span>
              <span v-if="item.duration">{{ formatDuration(item.duration) }}</span>
            </div>
          </div>

          <div class="media-card__actions">
            <el-button size="mini" type="primary" @click.stop="openPreview(item)">预览</el-button>
            <el-button
              size="mini"
              plain
              icon="el-icon-download"
              :disabled="!previewDownloadProxyUrl(item)"
              @click.stop="downloadItem(item)"
            >
              直接下载
            </el-button>
          </div>
        </article>
      </div>

      <div v-else-if="!searched && !loading" class="media-placeholder">
        输入关键词后即可开始搜索，也可以留空直接浏览推荐素材。
      </div>
    </section>

    <div v-if="searched && meta.totalHits > form.perPage" class="media-pagination">
      <el-pagination
        background
        layout="prev, pager, next, total"
        :current-page="form.page"
        :page-size="form.perPage"
        :total="meta.totalHits"
        @current-change="handlePageChange"
      />
    </div>

    <el-dialog
      :visible.sync="previewDialog.visible"
      width="960px"
      append-to-body
      custom-class="media-preview-dialog"
      @closed="handlePreviewClosed"
    >
      <div v-if="previewDialog.item" class="media-preview">
        <div class="media-preview__media">
          <img
            v-if="previewDialog.item.mediaType === 'image'"
            :src="previewDialog.item.displayUrl || previewDialog.item.previewUrl"
            :alt="previewDialog.item.title"
          />
          <video
            v-else
            ref="previewVideo"
            :src="previewDialog.item.displayUrl || previewDialog.item.previewUrl"
            :poster="previewDialog.item.previewImageUrl || ''"
            controls
            preload="metadata"
            class="media-preview__video"
          ></video>
        </div>

        <div class="media-preview__info">
          <div class="media-preview__badge-row">
            <el-tag size="small" effect="dark">{{ previewDialog.item.mediaType === 'image' ? '图片' : '视频' }}</el-tag>
          </div>

          <div class="media-preview__meta">
            <span v-if="previewDialog.item.width && previewDialog.item.height">
              尺寸：{{ previewDialog.item.width }} × {{ previewDialog.item.height }}
            </span>
            <span v-if="previewDialog.item.duration">时长：{{ formatDuration(previewDialog.item.duration) }}</span>
          </div>

          <div class="media-preview__actions">
            <el-button
              type="primary"
              icon="el-icon-download"
              :disabled="!previewDownloadProxyUrl(previewDialog.item)"
              @click="downloadPreviewItem"
            >
              {{ previewDialog.item.mediaType === 'image' ? '下载高清原图' : '下载高清原视频' }}
            </el-button>
          </div>

          <div class="media-preview__tip">
            当前内容支持在线预览，也可直接下载高清原始资源。
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="previewDialog.visible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
const PIXABAY_CATEGORY_OPTIONS = [
  { label: '背景', value: 'backgrounds' },
  { label: '时尚', value: 'fashion' },
  { label: '自然', value: 'nature' },
  { label: '科学', value: 'science' },
  { label: '教育', value: 'education' },
  { label: '情感', value: 'feelings' },
  { label: '健康', value: 'health' },
  { label: '人物', value: 'people' },
  { label: '宗教', value: 'religion' },
  { label: '地点', value: 'places' },
  { label: '动物', value: 'animals' },
  { label: '工业', value: 'industry' },
  { label: '电脑', value: 'computer' },
  { label: '美食', value: 'food' },
  { label: '运动', value: 'sports' },
  { label: '交通', value: 'transportation' },
  { label: '旅行', value: 'travel' },
  { label: '建筑', value: 'buildings' },
  { label: '商业', value: 'business' },
  { label: '音乐', value: 'music' }
]

const PIXABAY_COLOR_OPTIONS = [
  { label: '灰度', value: 'grayscale' },
  { label: '透明', value: 'transparent' },
  { label: '红色', value: 'red' },
  { label: '橙色', value: 'orange' },
  { label: '黄色', value: 'yellow' },
  { label: '绿色', value: 'green' },
  { label: '青绿色', value: 'turquoise' },
  { label: '蓝色', value: 'blue' },
  { label: '淡紫', value: 'lilac' },
  { label: '粉色', value: 'pink' },
  { label: '白色', value: 'white' },
  { label: '灰色', value: 'gray' },
  { label: '黑色', value: 'black' },
  { label: '棕色', value: 'brown' }
]

const PEXELS_COLOR_OPTIONS = [
  { label: '红色', value: 'red' },
  { label: '橙色', value: 'orange' },
  { label: '黄色', value: 'yellow' },
  { label: '绿色', value: 'green' },
  { label: '蓝色', value: 'blue' },
  { label: '紫色', value: 'violet' },
  { label: '粉色', value: 'pink' },
  { label: '棕色', value: 'brown' },
  { label: '黑色', value: 'black' },
  { label: '灰色', value: 'gray' },
  { label: '白色', value: 'white' }
]

export default {
  name: 'PixabaySearch',
  data() {
    return {
      loading: false,
      searched: false,
      items: [],
      meta: {
        totalHits: 0,
        queryUsed: '',
        originalQuery: '',
        translatedQuery: ''
      },
      form: {
        source: 'pexels',
        q: '',
        type: 'video',
        perPage: 24,
        order: 'popular',
        category: '',
        imageType: 'photo',
        orientation: 'all',
        videoType: 'all',
        colors: [],
        editorsChoice: false,
        minWidth: 0,
        minHeight: 0,
        safeSearch: true,
        pexelsOrientation: '',
        pexelsSize: '',
        pexelsLocale: 'zh-CN',
        pexelsColor: '',
        page: 1
      },
      previewDialog: {
        visible: false,
        item: null
      },
      quickKeywords: [
        { label: '自然', value: 'nature', type: 'image' },
        { label: '花朵', value: 'flowers', type: 'image' },
        { label: '背景', value: 'background', type: 'image' },
        { label: '森林', value: 'forest', type: 'image' },
        { label: '日落', value: 'sunset', type: 'image' },
        { label: '猫咪', value: 'cat', type: 'image' },
        { label: '风景', value: 'landscape', type: 'image' },
        { label: '天空', value: 'sky', type: 'image' },
        { label: '春天', value: 'spring', type: 'image' },
        { label: '复活节', value: 'easter', type: 'image' },
        { label: '山脉', value: 'mountains', type: 'image' },
        { label: '壁纸', value: 'wallpaper', type: 'image' },
        { label: 'iPhone壁纸', value: 'iphone wallpaper', type: 'image' }
      ],
      pixabayCategoryOptions: PIXABAY_CATEGORY_OPTIONS,
      pixabayColorOptions: PIXABAY_COLOR_OPTIONS,
      pexelsColorOptions: PEXELS_COLOR_OPTIONS
    }
  },
  computed: {
    isImageMode() {
      return this.form.type === 'image'
    },
    isPixabay() {
      return this.form.source === 'pixabay'
    },
    activeModeLabel() {
      if (this.form.type === 'video') return '视频'
      if (this.isPixabay && this.form.imageType === 'illustration') return '插画'
      if (this.isPixabay && this.form.imageType === 'vector') return '矢量图'
      return '图片'
    },
    translationSummary() {
      const parts = []
      if (this.meta.originalQuery) parts.push(`原词：${this.meta.originalQuery}`)
      if (this.meta.translatedQuery) parts.push(`映射：${this.meta.translatedQuery}`)
      if (this.meta.queryUsed && this.meta.queryUsed !== this.meta.originalQuery) {
        parts.push(`实际搜索：${this.meta.queryUsed}`)
      }
      return parts.join(' | ')
    }
  },
  created() {
    this.search(true)
  },
  beforeDestroy() {
    this.handlePreviewClosed()
  },
  methods: {
    buildParams() {
      if (this.isPixabay) {
        return {
          q: this.form.q,
          type: this.form.type,
          page: this.form.page,
          perPage: this.form.perPage,
          order: this.form.order,
          category: this.form.category,
          imageType: this.form.imageType,
          orientation: this.form.orientation,
          videoType: this.form.videoType,
          colors: this.form.colors.join(','),
          editorsChoice: this.form.editorsChoice,
          minWidth: this.form.minWidth,
          minHeight: this.form.minHeight,
          safeSearch: this.form.safeSearch
        }
      }
      return {
        q: this.form.q,
        type: this.form.type,
        page: this.form.page,
        perPage: this.form.perPage,
        orientation: this.form.pexelsOrientation,
        size: this.form.pexelsSize,
        locale: this.form.pexelsLocale,
        color: this.isImageMode ? this.form.pexelsColor : ''
      }
    },
    search(resetPage) {
      if (resetPage) this.form.page = 1
      this.loading = true
      const endpoint = this.isPixabay ? '/pixabay/search' : '/pexels/search'
      this.$axios.get(endpoint, {
        params: this.buildParams()
      }).then((res) => {
        const data = res.data || {}
        this.items = Array.isArray(data.items) ? data.items : []
        this.meta = {
          totalHits: Number(data.totalHits) || 0,
          queryUsed: data.queryUsed || '',
          originalQuery: data.originalQuery || '',
          translatedQuery: data.translatedQuery || ''
        }
        this.searched = true
      }).catch((err) => {
        const msg = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : '素材搜索失败'
        this.items = []
        this.meta = {
          totalHits: 0,
          queryUsed: '',
          originalQuery: '',
          translatedQuery: ''
        }
        this.searched = true
        this.$message.error(msg)
      }).finally(() => {
        this.loading = false
      })
    },
    onSourceChange() {
      this.form.page = 1
      if (this.form.source === 'pexels') {
        this.form.type = 'video'
      }
      this.search(false)
    },
    onTypeChange() {
      this.form.page = 1
      this.search(false)
    },
    handlePageChange(page) {
      this.form.page = page
      this.search(false)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    applyQuickKeyword(item) {
      this.form.q = item.value
      this.form.type = item.type || this.form.type
      this.search(true)
    },
    openPreview(item) {
      if (!item) return
      this.previewDialog.item = item
      this.previewDialog.visible = true
    },
    handlePreviewClosed() {
      const video = this.$refs.previewVideo
      if (video && video.pause) video.pause()
      this.previewDialog.item = null
    },
    previewDownloadProxyUrl(item) {
      const target = item && (item.downloadUrl || item.displayUrl || item.previewUrl)
      if (!target) return ''
      const params = new URLSearchParams()
      params.set('url', target)
      params.set('mediaType', item.mediaType === 'video' ? 'video' : 'image')
      if (item.downloadFilename) params.set('filename', item.downloadFilename)
      return `/media-resource/download?${params.toString()}`
    },
    downloadItem(item) {
      const url = this.previewDownloadProxyUrl(item)
      if (!url) {
        this.$message.warning('当前资源暂时无法下载')
        return
      }
      try {
        const a = document.createElement('a')
        a.href = url
        a.rel = 'noopener noreferrer'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        this.$message.success(item && item.mediaType === 'video' ? '已开始下载高清原视频' : '已开始下载高清原图')
      } catch (e) {
        window.location.href = url
      }
    },
    downloadPreviewItem() {
      this.downloadItem(this.previewDialog.item)
    },
    videoPosterStyle(item) {
      return item && item.previewImageUrl ? { backgroundImage: `url(${item.previewImageUrl})` } : {}
    },
    formatNumber(value) {
      const n = Number(value) || 0
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
      if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
      return String(n)
    },
    formatDuration(value) {
      const sec = Math.max(0, parseInt(value, 10) || 0)
      const minute = Math.floor(sec / 60)
      const second = `${sec % 60}`.padStart(2, '0')
      return `${minute}:${second}`
    }
  }
}
</script>

<style scoped lang="less">
.media-search {
  padding: 8px 0 22px;
  color: var(--text-primary, #2d3436);
}

.media-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(260px, 0.8fr);
  gap: 20px;
  padding: 28px;
  border-radius: 28px;
  background:
    radial-gradient(circle at left top, rgba(255, 195, 113, 0.34), transparent 36%),
    radial-gradient(circle at right bottom, rgba(68, 173, 255, 0.26), transparent 32%),
    linear-gradient(135deg, #fff9ef 0%, #f5f8ff 52%, #f7fbff 100%);
  box-shadow: 0 22px 48px rgba(46, 86, 150, 0.12);
  border: 1px solid rgba(255, 178, 92, 0.18);
}

.media-hero__copy h2 {
  margin: 14px 0 12px;
  font-size: 34px;
  line-height: 1.1;
}

.media-hero__copy p {
  margin: 0;
  max-width: 720px;
  font-size: 15px;
  line-height: 1.85;
  color: var(--text-regular, #606266);
}

.media-hero__badge {
  display: inline-flex;
  align-items: center;
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(255, 153, 51, 0.16);
  color: #d96b00;
  font-size: 12px;
  font-weight: 700;
}

.media-hero__note {
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.72);
  color: #6a5560;
  line-height: 1.7;
}

.media-hero__stats {
  display: grid;
  gap: 14px;
  align-content: center;
}

.media-stat {
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: inset 0 0 0 1px rgba(108, 145, 255, 0.08);
}

.media-stat strong {
  display: block;
  font-size: 24px;
  line-height: 1.15;
  color: #244e8f;
}

.media-stat span {
  display: block;
  margin-top: 7px;
  color: var(--text-secondary, #909399);
  font-size: 13px;
}

.media-panel {
  margin-top: 18px;
  padding: 22px;
  border-radius: 24px;
  background: #fff;
  border: 1px solid rgba(80, 119, 197, 0.1);
  box-shadow: 0 16px 34px rgba(34, 74, 138, 0.08);
}

.media-toolbar {
  display: grid;
  gap: 14px;
}

.media-filters,
.media-advanced {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
  align-items: center;
}

.media-filter-select {
  width: 132px;
}

.media-alert {
  margin-top: 16px;
  border-radius: 14px;
}

.media-advanced {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(80, 119, 197, 0.08);
}

.media-advanced__group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
}

.media-advanced__group--wide {
  flex: 1;
  min-width: 260px;
}

.media-advanced__label,
.media-advanced__tip {
  color: #657387;
  font-size: 13px;
  line-height: 1.6;
}

.media-color-select {
  min-width: 220px;
}

.media-advanced__sep {
  color: #7c8796;
}

.media-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.media-quick__chip {
  padding: 8px 14px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #f3f7ff 0%, #eefaf7 100%);
  color: #2c5c8d;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  box-shadow: inset 0 0 0 1px rgba(82, 130, 214, 0.12);
}

.media-quick__chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(69, 112, 184, 0.12);
}

.media-translation {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 14px;
  background: rgba(255, 244, 225, 0.9);
  color: #9a5c00;
  line-height: 1.6;
}

.media-results {
  min-height: 320px;
  margin-top: 18px;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 18px;
}

.media-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 22px;
  background: #fff;
  border: 1px solid rgba(88, 110, 165, 0.1);
  box-shadow: 0 14px 30px rgba(44, 72, 126, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.media-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 38px rgba(35, 68, 126, 0.14);
}

.media-card__media {
  position: relative;
  aspect-ratio: 16 / 10;
  background: linear-gradient(135deg, #edf4ff 0%, #f8fbff 100%);
  overflow: hidden;
}

.media-card__media img,
.media-preview__media img,
.media-preview__video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.media-card__video {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #16253d;
  background-size: cover;
  background-position: center;
}

.media-card__video::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(7, 16, 30, 0.05), rgba(7, 16, 30, 0.56));
}

.media-card__play {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  color: #173960;
  font-weight: 600;
}

.media-card__body {
  flex: 1;
  padding: 16px 18px 14px;
}

.media-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.media-card__body h3 {
  margin: 12px 0 8px;
  font-size: 19px;
  line-height: 1.35;
  color: #213547;
  word-break: break-word;
}

.media-card__tags {
  margin: 0;
  min-height: 44px;
  color: #6a7485;
  font-size: 13px;
  line-height: 1.7;
}

.media-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 12px;
  color: #7d8797;
  font-size: 12px;
}

.media-card__actions {
  display: flex;
  gap: 10px;
  padding: 0 18px 18px;
}

.media-placeholder {
  padding: 56px 20px;
  border-radius: 22px;
  text-align: center;
  color: #8390a6;
  background: linear-gradient(135deg, rgba(248, 251, 255, 0.92), rgba(250, 248, 242, 0.92));
  border: 1px dashed rgba(104, 127, 180, 0.2);
}

.media-pagination {
  display: flex;
  justify-content: center;
  margin-top: 22px;
}

.media-preview {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(260px, 0.8fr);
  gap: 20px;
}

.media-preview__media {
  overflow: hidden;
  border-radius: 22px;
  background: #111a29;
  min-height: 320px;
}

.media-preview__info {
  display: flex;
  flex-direction: column;
  gap: 14px;
  justify-content: center;
}

.media-preview__badge-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.media-preview__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.media-preview__actions .el-button {
  min-width: 160px;
}

.media-preview__info p {
  margin: 0;
  color: #5f6b7a;
  line-height: 1.8;
}

.media-preview__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  color: #687587;
  font-size: 13px;
}

.media-preview__tip {
  padding: 12px 14px;
  border-radius: 14px;
  background: #f7f8fc;
  color: #66717f;
  line-height: 1.7;
}

.media-empty {
  padding: 46px 0 24px;
  background: #fff;
  border-radius: 22px;
  border: 1px solid rgba(89, 115, 172, 0.08);
}

.dark-mode .media-hero {
  background:
    radial-gradient(circle at left top, rgba(255, 188, 88, 0.2), transparent 34%),
    radial-gradient(circle at right bottom, rgba(78, 173, 255, 0.16), transparent 32%),
    linear-gradient(135deg, #172130 0%, #101824 58%, #111a28 100%);
  border-color: rgba(255, 185, 94, 0.16);
  box-shadow: 0 22px 48px rgba(0, 0, 0, 0.34);
}

.dark-mode .media-hero__copy p,
.dark-mode .media-hero__note,
.dark-mode .media-stat span,
.dark-mode .media-placeholder,
.dark-mode .media-card__tags,
.dark-mode .media-card__meta,
.dark-mode .media-preview__info p,
.dark-mode .media-preview__meta,
.dark-mode .media-preview__tip,
.dark-mode .media-advanced__label,
.dark-mode .media-advanced__tip {
  color: rgba(232, 239, 248, 0.78);
}

.dark-mode .media-stat,
.dark-mode .media-panel,
.dark-mode .media-card,
.dark-mode .media-empty,
.dark-mode .media-preview__tip {
  background: rgba(17, 27, 42, 0.92);
  border-color: rgba(124, 150, 193, 0.14);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.28);
}

.dark-mode .media-card__body h3,
.dark-mode .media-preview__actions .el-button {
  box-shadow: none;
}

.dark-mode .media-translation {
  background: rgba(82, 53, 6, 0.72);
  color: #ffd58b;
}

.dark-mode .media-quick__chip {
  background: linear-gradient(135deg, rgba(35, 52, 79, 0.96), rgba(24, 49, 43, 0.96));
  color: #dbe8fb;
  box-shadow: inset 0 0 0 1px rgba(132, 161, 215, 0.12);
}

@media (max-width: 1080px) {
  .media-hero,
  .media-preview {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .media-search {
    padding-bottom: 14px;
  }

  .media-hero,
  .media-panel {
    padding: 18px;
    border-radius: 20px;
  }

  .media-hero__copy h2 {
    font-size: 28px;
  }

  .media-grid {
    grid-template-columns: 1fr;
  }

  .media-card__actions {
    flex-wrap: wrap;
  }

  .media-filter-select,
  .media-color-select {
    width: 100%;
  }

  .media-pagination :deep(.el-pagination) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 8px 6px;
    white-space: normal;
  }

  .media-pagination :deep(.el-pagination__total) {
    flex: 0 0 100%;
    margin: 0;
    text-align: center;
  }

  .media-pagination :deep(.btn-prev),
  .media-pagination :deep(.btn-next),
  .media-pagination :deep(.el-pager li) {
    margin: 0;
    min-width: 30px;
  }
}
</style>
